import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import type { PlatformBridge, ViewMode } from '@slate/core';
import {
  BookOpen,
  ChevronDown,
  Columns2,
  FilePenLine,
  FileText,
  Save,
  SaveAll,
} from 'lucide-react';
import { useDocStore, isLargeDoc, useActiveText } from '../store/docStore';
import { saveActiveDoc, saveActiveDocAs } from '../store/docActions';
import { shortcutLabel, viewSwitchLabel } from '../keymap/platform';
import { HelpLink } from './HelpLink';
import { ShortcutBadge } from './ShortcutBadge';
import { PluginTopBarActions } from '../plugins/PluginTopBarActions';
import type { PluginHost } from '../plugins/PluginHost';

const VIEW_OPTIONS: {
  mode: ViewMode;
  label: string;
  Icon: typeof FileText;
  shortcutKey: string;
}[] = [
  { mode: 'plaintext', label: 'Plain', Icon: FileText, shortcutKey: '1' },
  { mode: 'wysiwyg', label: 'WYSIWYG', Icon: FilePenLine, shortcutKey: '2' },
  { mode: 'rendered', label: 'Reading', Icon: BookOpen, shortcutKey: '3' },
];

export interface ToolbarProps {
  platform: PlatformBridge;
  /**
   * When the active document is a non-markdown code/text file only the plain-text
   * editor makes sense — the view-mode segmented control and Split are HIDDEN
   * (not just disabled). Only Save remains. The outline toggle lives in the
   * title bar (next to Settings) and is gated separately.
   */
  editorOnly?: boolean;
  /** The plugin host, so manifest-declared header actions render before Help. */
  pluginHost?: PluginHost;
}

/**
 * Save split/combo button: a primary "Save" joined to a chevron that opens a
 * small menu with "Save As…". Save uses the doc's existing ref (falling back to
 * Save As when there's none); the menu's Save As always prompts for a path.
 * Dismisses on outside pointer / Escape / window blur; keyboard accessible.
 */
function SaveSplitButton({ platform }: { platform: PlatformBridge }): ReactElement {
  const [open, setOpen] = useState(false);
  // Viewport coordinates for the portaled menu, anchored under the chevron.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // First focusable item the menu lands on when it opens (Save As…).
  const itemRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  // Autosave toggle moved to Settings → Save; only the on-Save indicator
  // remains here so the user can tell autosave is active at a glance.
  const autosave = useDocStore((s) => s.autosave);

  const close = useCallback((focusChevron: boolean) => {
    setOpen(false);
    if (focusChevron) chevronRef.current?.focus();
  }, []);

  // Anchor the portaled menu under the chevron (right-aligned to the trigger).
  // Measured before paint so there's no jump.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = chevronRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({
      top: rect.bottom + 6,
      right: Math.max(0, (window.innerWidth || 0) - rect.right),
    });
  }, [open]);

  // Dismiss on outside pointer / window blur — only while open. The menu is
  // portaled to <body>, so treat clicks inside it as inside too.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onWindowBlur = (): void => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [open]);

  // Move focus into the menu's single item when it opens.
  useEffect(() => {
    if (open) itemRef.current?.focus();
  }, [open]);

  const onSave = (): void => void saveActiveDoc(platform);
  const onSaveAs = (): void => {
    void saveActiveDocAs(platform);
    close(true);
  };

  const onChevronKeyDown = useCallback(
    (e: ReactKeyboardEvent): void => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (open) close(false);
        else setOpen(true);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        close(true);
      }
    },
    [open, close],
  );

  const onMenuKeyDown = useCallback(
    (e: ReactKeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      } else if (e.key === 'Tab') {
        setOpen(false); // let focus leave naturally
      }
    },
    [close],
  );

  return (
    <div className="mp-btn-combo" ref={rootRef}>
      <button
        type="button"
        className="mp-btn mp-btn--primary mp-btn-combo__main"
        // When autosave is on, paint a tiny pulsing dot at the corner of the
        // Save icon (not the label) so the indicator is anchored to the
        // affordance it modifies. Driven by the `::after` on
        // `.mp-btn-combo__icon` in theme/index.css.
        data-autosave={autosave ? '' : undefined}
        title={autosave ? 'Save (autosave is on)' : 'Save'}
        aria-label={autosave ? 'Save (autosave is on)' : 'Save'}
        onClick={onSave}
      >
        <span className="mp-btn-combo__icon" aria-hidden>
          <Save size={15} aria-hidden />
        </span>
        Save
      </button>
      <button
        ref={chevronRef}
        type="button"
        className="mp-btn mp-btn--primary mp-btn-combo__chevron"
        aria-label="More save options"
        title="More save options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onChevronKeyDown}
      >
        <ChevronDown size={14} aria-hidden />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="mp-save-menu"
            id={menuId}
            role="menu"
            aria-label="Save options"
            style={{ top: menuPos.top, right: menuPos.right }}
            onKeyDown={onMenuKeyDown}
          >
            <button
              ref={itemRef}
              type="button"
              role="menuitem"
              className="mp-save-menu__item"
              onClick={onSaveAs}
            >
              <SaveAll size={14} aria-hidden />
              Save As…
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Toolbar({
  platform,
  editorOnly = false,
  pluginHost,
}: ToolbarProps): ReactElement {
  const viewMode = useDocStore((s) => s.viewMode);
  const split = useDocStore((s) => s.split);
  const setViewMode = useDocStore((s) => s.setViewMode);
  const toggleSplit = useDocStore((s) => s.toggleSplit);
  // WYSIWYG (Crepe/ProseMirror) doesn't scale past ~500 KB — disable it for
  // very large docs (spec §4.1). The doc still opens in plaintext+rendered.
  const wysiwygDisabled = isLargeDoc(useActiveText());

  return (
    <div className="mp-toolbar">
      {/* View-mode controls only make sense for markdown — hidden for code/text.
          Order in this row: [view-mode segmented] → [Split] → [HelpLink]. */}
      {!editorOnly && (
        <>
          <div className="mp-segmented" role="group" aria-label="View mode">
            {VIEW_OPTIONS.map(({ mode, label, Icon, shortcutKey }) => {
              const disabled = mode === 'wysiwyg' && wysiwygDisabled;
              return (
                <button
                  key={mode}
                  type="button"
                  className="mp-segmented__btn"
                  aria-pressed={viewMode === mode}
                  aria-keyshortcuts={viewSwitchLabel(shortcutKey)}
                  disabled={disabled}
                  title={
                    disabled
                      ? 'WYSIWYG is disabled for very large files — use Plain or Reading.'
                      : `${label} view (${viewSwitchLabel(shortcutKey)})`
                  }
                  onClick={() => setViewMode(mode)}
                >
                  <Icon size={14} aria-hidden strokeWidth={2} />
                  {label}
                  {/* Shortcut shown only in reveal mode (mod held ~1s); the
                      binding stays on title + aria-keyshortcuts at all times. */}
                  <ShortcutBadge label={viewSwitchLabel(shortcutKey)} />
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="mp-btn"
            aria-pressed={split}
            aria-keyshortcuts={shortcutLabel('\\')}
            onClick={toggleSplit}
            disabled={viewMode === 'rendered'}
            title={`Toggle split with rendered preview (${shortcutLabel('\\')})`}
          >
            <Columns2 size={15} aria-hidden />
            Split
            <ShortcutBadge label={shortcutLabel('\\')} />
          </button>

          {pluginHost && <PluginTopBarActions host={pluginHost} />}

          <HelpLink
            platform={platform}
            anchor="views#switching-views"
            label="Learn about the three views"
          />
        </>
      )}

      <div className="mp-toolbar__actions">
        <SaveSplitButton platform={platform} />
      </div>
    </div>
  );
}
